/**
 * Supplier-side onboarding view showing their own questionnaires and workflow progress.
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ClipboardList,
  Building2,
  RefreshCw,
  ArrowRight,
  Eye,
} from 'lucide-react';
import { FilledDataset, OnboardingWorkflow } from '../../lib/api';
import { statusColors } from './onboardingConstants';

interface SupplierOnboardingViewProps {
  myFilledDatasets: FilledDataset[];
  myWorkflow: OnboardingWorkflow | null;
  onRefresh: () => void;
}

export function SupplierOnboardingView({
  myFilledDatasets,
  myWorkflow,
  onRefresh,
}: SupplierOnboardingViewProps) {
  const navigate = useNavigate();

  const completedCount = myFilledDatasets.filter(d => d.status === 'approved' || d.status === 'submitted').length;
  const pendingCount = myFilledDatasets.filter(d => d.status === 'not_started' || d.status === 'in_progress').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Onboarding</h1>
          <p className="text-muted-foreground">
            Complete your onboarding questionnaires and requirements
          </p>
        </div>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Workflow Status Card */}
      {myWorkflow && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Onboarding Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Status</p>
                <Badge className={statusColors[myWorkflow.status] || statusColors['pending']}>
                  {myWorkflow.status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Progress</p>
                <div className="flex items-center gap-2">
                  <Progress value={myWorkflow.progress_percentage || 0} className="w-32" />
                  <span className="text-sm font-medium">{Math.round(myWorkflow.progress_percentage || 0)}%</span>
                </div>
              </div>
            </div>
            {myWorkflow.customer_name && (
              <div>
                <p className="text-sm text-muted-foreground">Onboarding for</p>
                <p className="font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {myWorkflow.customer_name}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">Questionnaires to complete</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Submitted</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {myFilledDatasets.filter(d => d.status === 'submitted' || d.status === 'under_review').length}
            </div>
            <p className="text-xs text-muted-foreground">Awaiting review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedCount}</div>
            <p className="text-xs text-muted-foreground">Completed questionnaires</p>
          </CardContent>
        </Card>
      </div>

      {/* Questionnaires List */}
      <Card>
        <CardHeader>
          <CardTitle>My Questionnaires</CardTitle>
          <CardDescription>
            Complete the following questionnaires to finish your onboarding
          </CardDescription>
        </CardHeader>
        <CardContent>
          {myFilledDatasets.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No questionnaires assigned</h3>
              <p className="text-muted-foreground">
                You don't have any questionnaires to complete at this time
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {myFilledDatasets.map((filled) => (
                <div
                  key={filled.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${
                      filled.status === 'approved' ? 'bg-green-100' :
                      filled.status === 'submitted' || filled.status === 'under_review' ? 'bg-blue-100' :
                      filled.status === 'in_progress' ? 'bg-yellow-100' :
                      'bg-gray-100'
                    }`}>
                      {filled.status === 'approved' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : filled.status === 'submitted' || filled.status === 'under_review' ? (
                        <Clock className="h-5 w-5 text-blue-600" />
                      ) : (
                        <FileText className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium">{filled.dataset_name || 'Questionnaire'}</h4>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant="outline">{filled.dataset_type || 'general'}</Badge>
                        {filled.completion_percentage !== undefined && (
                          <span>{Math.round(filled.completion_percentage)}% complete</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusColors[filled.status] || statusColors['pending']}>
                      {filled.status?.replace(/_/g, ' ')}
                    </Badge>
                    {(filled.status === 'not_started' || filled.status === 'in_progress' || filled.status === 'more_info_required') && (
                      <Button onClick={() => navigate(`/onboarding/fill/${filled.id}`)}>
                        {filled.status === 'not_started' ? 'Start' : 'Continue'}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                    {filled.status === 'submitted' || filled.status === 'under_review' || filled.status === 'approved' && (
                      <Button variant="outline" onClick={() => navigate(`/onboarding/fill/${filled.id}`)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
